import { useState } from 'react';

import {
  Anchor,
  Box,
  Button,
  Icon,
  IconButton,
  Menu,
  Paragraph,
  PropertyCard,
  Table,
  Tag,
  Text,
} from '@zillow/constellation-10';
import {
  IconCheckmarkCircleFilled,
  IconHideFilled,
  IconHourglassFilled,
  IconMoreFilled,
  IconWarningFilled,
  IconZillowMark,
} from '@zillow/constellation-icons';

import { LoadingParagraph, LoadingText } from '@zg-rentals/loading-components';
import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  AccountViewListingOptionType,
  RentalManagerUserPropertyDetailsSyndicationOverviewStatus,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useRentalManagerAccountViewZramMultifamilyWebApiHandler_advertising,
  useRentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising,
  useRentalManagerUserPropertySpotlightWebApiHandler_list,
  useRentalManagerUserPropertySpotlightWebApiHandler_overview,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import stringHelper from '../../../utils/stringHelper';
import {
  useZramAdvertising,
  useZramListingQualityScore,
  useZramOptions,
  useZramProspects,
  useZramSources,
  useZramStatus,
} from '../../../hooks/queries/properties/zram';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { getPhotoUrls } from '../../../utils/propertiesPageUtils';
import propertyUtils from '../../../utils/propertyUtils';

import QueryConstants from '../../../../srcLegacy/shared/constants/QueryConstants';
import { RecsLearnMoreConstants } from '../../../../srcLegacy/shared/constants/RecommendationsConstants';
import {
  emitIncreaseYourExposureEvent,
  emitReleaseClaimEvent,
  INCREASE_YOUR_EXPOSURE_EVENT_ID,
  RELEASE_CLAIM_EVENT_ID,
} from '../../../analytics/events/claimDashboard';
import { ROUTES } from '../../../constants/urls';

import { useAuth } from '../../../components/AuthProvider';
import ArchivePropertyModal from '../../../components/modals/ArchivePropertyModal';
import ReleaseClaimModal from '../../../components/modals/ReleaseClaimModal';
import SpotlightApplyModal from '../../../components/modals/SpotlightApplyModal';
import SpotlightReassignModal from '../../../components/modals/SpotlightReassignModal';
import SpotlightRemovalModal from '../../../components/modals/SpotlightRemovalModal';

import {
  addressTextStyles,
  addressWrapperStyles,
  hiddenMobileTableCellStyles,
  leftDataCellStyles,
  listingQualityContainerStyles,
  listingQualityContentRecipe,
  photoStyles,
  rightDataCellStyles,
  statusIconStyles,
} from '../styles';

import {
  emitPropertyCardOverflowMenuEvent,
  EVENT_IDS as PROPERTIES_PAGE_EVENT_IDS,
} from '../../../analytics/events/propertiesPage';
import {
  emitAddImprovementClickEvent,
  emitChangeAdProductEvent,
  emitPropertyCardClickZRAMEvent,
  EVENT_IDS as ZRAM_PROPERTIES_PAGE_EVENT_IDS,
} from '../../../analytics/events/zramPropertiesPage';

import type {
  AccountViewListingAdproductDto,
  AccountViewListingAdvertisingDto,
  AccountViewListingDto,
  AccountViewListingOptionDto,
  AccountViewListingStatusOverviewDto,
} from '@zg-rentals/ts-rental-manager-api-client';

// Type guard to check if advertising data has button text and URL
const hasAdvertisingButtons = (
  advertising: AccountViewListingAdvertisingDto | AccountViewListingAdproductDto | undefined | null,
): advertising is AccountViewListingAdvertisingDto => {
  return advertising != null && 'buttonText' in advertising && 'buttonUrl' in advertising;
};

export const PropertyRow = ({
  listing,
  isScrolledToLeft,
  isLoading: isComponentLoading,
  isArchivedView,
}: {
  listing: AccountViewListingDto;
  isScrolledToLeft: boolean;
  isLoading: boolean;
  isArchivedView: boolean;
}) => {
  const { user } = useAuth();
  const { isScreenXxlGTE } = useScreenSize();

  const isRunningInProd =
    typeof window !== 'undefined'
      ? window.location.hostname.includes('zillow.com') || window.location.hostname.includes('hotpads.com')
      : false;

  const [displayReleaseClaimModal, setDisplayReleaseClaimModal] = useState(false);
  const [displayArchivePropertyModal, setDisplayArchivePropertyModal] = useState('');
  const [displaySpotlightApplyModal, setDisplaySpotlightApplyModal] = useState(false);
  const [displaySpotlightRemoveModal, setDisplaySpotlightRemoveModal] = useState(false);
  const [displaySpotlightReassignModal, setDisplaySpotlightReassignModal] = useState(false);

  const { address, name, photoKey, isMultifamily, listingAlias } = listing;

  const { data: statusData, isPending: isStatusPending } = useZramStatus(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) },
  );
  const isListingInactive = statusData?.status === RentalManagerUserPropertyDetailsSyndicationOverviewStatus.INACTIVE;

  const { data: listingQualityScore, isFetching: isListingQualityScoreFetching } = useZramListingQualityScore(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) && !isArchivedView && !(isStatusPending || isListingInactive) },
  );

  const { data: prospects, isFetching: isProspectsFetching } = useZramProspects(
    {
      listingAlias,
    },
    {
      enabled: Boolean(listingAlias) && !isArchivedView && !(isStatusPending || isListingInactive),
    },
  );

  const { data: advertising, isFetching: isAdvertisingFetching } = useZramAdvertising(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) && !isArchivedView },
  );

  const { data: sources, isPending: isSourcesPending } = useZramSources(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) },
  );
  const { data: { options = [] } = {}, isPending: isOptionsPending } = useZramOptions(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) },
  );
  const { data: webAnalyticsData } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
    },
    { enabled: Boolean(listingAlias) },
  );
  const { data: spotlightOverviewData } = useRentalManagerUserPropertySpotlightWebApiHandler_overview();

  const { formattedStreet, formattedCityStateZip } = propertyUtils.formatAddress({
    ...address,
  });

  const baseRoute = `/rental-manager${ROUTES.PROPERTIES}/${listingAlias}`;
  const targetListingRoute = isMultifamily ? `${baseRoute}/listing` : `${baseRoute}/dashboard/overview`;

  const adProductDataEventId =
    hasAdvertisingButtons(advertising) && advertising.buttonText === 'Increase your exposure'
      ? INCREASE_YOUR_EXPOSURE_EVENT_ID
      : undefined;

  const spotlightAvailabilityText = `${spotlightOverviewData?.availableCount} of ${advertising?.listingSpotlight?.totalCount} available`;

  const routerHandler = (pathToRoute: string, openNewTab = false) => {
    if (openNewTab) {
      window.open(pathToRoute, '_blank');
    } else {
      window.location.href = pathToRoute;
    }
  };

  const handleInnerCTAClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const handleAdvertisingCTAClick = (ctaText: string) => {
    if (ctaText === 'Increase your exposure') {
      emitIncreaseYourExposureEvent({ user });
    }
    if (user) {
      emitChangeAdProductEvent({ user });
    }
  };

  const handleMenuClick = (open: boolean) => {
    if (open && user) {
      emitPropertyCardOverflowMenuEvent({
        user,
        currentProperty: webAnalyticsData ?? {},
      });
    }
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    option?: AccountViewListingOptionDto,
    ctaText?: string,
  ) => {
    handleInnerCTAClick(event);

    if (!option && ctaText) {
      handleAdvertisingCTAClick(ctaText);
      return;
    }

    if (
      option?.type === AccountViewListingOptionType.ARCHIVE ||
      option?.type === AccountViewListingOptionType.UNARCHIVE
    ) {
      setDisplayArchivePropertyModal(option?.type as AccountViewListingOptionType);
      return;
    }

    if (option?.type === AccountViewListingOptionType.RELEASE_CLAIM) {
      setDisplayReleaseClaimModal(true);
      emitReleaseClaimEvent({ user });
      return;
    }
  };

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const openNewTab = event.ctrlKey || event.metaKey;
    event.preventDefault();
    event.stopPropagation();

    if (user) {
      emitPropertyCardClickZRAMEvent({ user });
    }

    if (targetListingRoute) {
      routerHandler(targetListingRoute, openNewTab);
    }
  };

  const renderCellOrError = <T,>(
    data: T | undefined | null,
    isCellPending: boolean,
    renderContent: (data: T, showLoading: boolean) => React.ReactNode,
  ) => {
    const shouldShowLoading = isComponentLoading || isCellPending;
    if (!data && !shouldShowLoading) {
      return <Text textStyle="body-sm">-</Text>;
    }
    return renderContent(data as T, shouldShowLoading);
  };

  const renderMissingDetail = (missingDetail: string) => {
    if (missingDetail in RecsLearnMoreConstants) {
      return (
        <Box key={`${name}-${missingDetail}`}>
          <Anchor
            data-eventid={ZRAM_PROPERTIES_PAGE_EVENT_IDS.ADD_IMPROVEMENT_CLICK}
            href={`${baseRoute}/listing?${QueryConstants.SCROLL_TO}=${missingDetail}`}
            css={{ color: 'text.neutral' }}
            onClick={(event) => {
              handleInnerCTAClick(event);
              if (user) {
                emitAddImprovementClickEvent({ user });
              }
            }}
          >
            {RecsLearnMoreConstants[missingDetail].heading}
          </Anchor>
        </Box>
      );
    }
    logError({
      error: new Error(`Unknown missing detail type: ${missingDetail}`),
      errorType: 'PropertyRow.MissingDetailError',
      context: { listingAlias },
    });
    return null;
  };

  const getStatusIcon = (status: RentalManagerUserPropertyDetailsSyndicationOverviewStatus) => {
    switch (status) {
      case RentalManagerUserPropertyDetailsSyndicationOverviewStatus.WAITLISTED:
        return (
          <Icon size="sm" css={{ color: 'icon.muted', ...statusIconStyles }}>
            <IconHourglassFilled />
          </Icon>
        );
      case RentalManagerUserPropertyDetailsSyndicationOverviewStatus.DECLINED:
        return (
          <Icon size="sm" css={{ color: 'icon.express.empower.hero', ...statusIconStyles }}>
            <IconWarningFilled />
          </Icon>
        );
      case RentalManagerUserPropertyDetailsSyndicationOverviewStatus.INACTIVE:
        return (
          <Icon size="sm" css={{ color: 'icon.muted', ...statusIconStyles }}>
            <IconHideFilled />
          </Icon>
        );
      default:
        return (
          <Icon size="sm" css={{ color: 'icon.express.insight.hero', ...statusIconStyles }}>
            <IconCheckmarkCircleFilled />
          </Icon>
        );
    }
  };

  const renderStatusCell = (statusData: AccountViewListingStatusOverviewDto, showLoading: boolean) => {
    const { status, display, explanation } = statusData || {};

    return (
      <Box css={{ display: 'flex' }}>
        {status && getStatusIcon(status)}
        <Box>
          <LoadingText isLoading={showLoading} textStyle="body-sm-bold" loadingWidth="7rem">
            {display}
          </LoadingText>
          {explanation && <Paragraph textStyle="body-sm">{explanation}</Paragraph>}
        </Box>
      </Box>
    );
  };

  const renderAdvertisingCTA = (isFromActionsMenu = true) => {
    if (!hasAdvertisingButtons(advertising)) {
      return null;
    }

    const utmParam = advertising.buttonUrl.includes('advertise-zillow-rentals-zram')
      ? '?utm_source=zram&utm_medium=onsite&utm_campaign=listing_spotlight_zram&utm_content=mf_rentals_contact_sales'
      : '?utm_source=zram&utm_medium=onsite&utm_campaign=zram&utm_content=mf_rentals_contact_sales';

    return (
      <Button
        emphasis="outlined"
        size="sm"
        tone="neutral"
        asChild={true}
        onClick={(event) => {
          if (advertising.buttonUrl === 'SPOTLIGHT_APPLY') {
            event.preventDefault();
            event.stopPropagation();
            setDisplaySpotlightApplyModal(true);
            return;
          }
          if (advertising.buttonUrl === 'SPOTLIGHT_REMOVE') {
            event.preventDefault();
            event.stopPropagation();
            setDisplaySpotlightRemoveModal(true);
            return;
          }
          if (advertising.buttonUrl === 'SPOTLIGHT_REASSIGN') {
            event.preventDefault();
            event.stopPropagation();
            setDisplaySpotlightReassignModal(true);
            return;
          }

          handleInnerCTAClick(event);
          handleAdvertisingCTAClick(advertising.buttonText);
        }}
        data-eventid={ZRAM_PROPERTIES_PAGE_EVENT_IDS.CHANGE_AD_PRODUCT}
        css={{
          width: isFromActionsMenu ? 'max-content' : 'fit-content',
          marginTop: isFromActionsMenu ? undefined : 'tighter',
        }}
      >
        <a
          href={`${advertising.buttonUrl}${utmParam}`}
          data-eventid={adProductDataEventId}
          target="_blank"
          rel="noreferrer noopener"
        >
          {advertising.buttonText}
        </a>
      </Button>
    );
  };

  const queryClient = useQueryClient();
  const params = { listingAlias };

  const invalidateAdvertisingQueries = (
    patch: Pick<AccountViewListingAdvertisingDto, 'buttonText' | 'buttonUrl'> & {
      listingSpotlight: { isSpotlighted: boolean };
    },
  ) => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertySpotlightWebApiHandler_overview.getKey(),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertySpotlightWebApiHandler_list.getKey(),
    });

    const isSingleFamily = user?.isSingleFamilyEnterpriseCustomer;
    if (!isSingleFamily) {
      queryClient.invalidateQueries({
        queryKey: useRentalManagerAccountViewZramMultifamilyWebApiHandler_advertising.getKey(params).slice(0, 1),
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: useRentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising.getKey(params).slice(0, 1),
      });
    }

    const refetchKey = isSingleFamily
      ? useRentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising.getKey(params)
      : useRentalManagerAccountViewZramMultifamilyWebApiHandler_advertising.getKey(params);
    void queryClient
      .refetchQueries({ queryKey: refetchKey })
      .then(() => setAdvertisingCache(patch))
      .catch(() => setAdvertisingCache(patch));
  };

  const setAdvertisingCache = (
    patch: Pick<AccountViewListingAdvertisingDto, 'buttonText' | 'buttonUrl'> & {
      listingSpotlight: { isSpotlighted: boolean };
    },
  ) => {
    const updater = (old: AccountViewListingAdvertisingDto | undefined) => {
      if (!old) return old;
      return {
        ...old,
        buttonText: patch.buttonText,
        buttonUrl: patch.buttonUrl,
        listingSpotlight: {
          ...old.listingSpotlight,
          isSpotlighted: patch.listingSpotlight.isSpotlighted,
        },
      };
    };
    const key = user?.isSingleFamilyEnterpriseCustomer
      ? useRentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising.getKey(params)
      : useRentalManagerAccountViewZramMultifamilyWebApiHandler_advertising.getKey(params);
    queryClient.setQueryData(key, updater);
  };

  const handleSpotlightApplySuccess = () => {
    const patch = {
      buttonText: 'Remove Spotlight',
      buttonUrl: 'SPOTLIGHT_REMOVE',
      listingSpotlight: { isSpotlighted: true },
    };
    invalidateAdvertisingQueries(patch);
    setDisplaySpotlightApplyModal(false);
  };

  const handleSpotlightRemoveSuccess = () => {
    const patch = {
      buttonText: 'Apply Spotlight',
      buttonUrl: 'SPOTLIGHT_APPLY',
      listingSpotlight: { isSpotlighted: false },
    };
    invalidateAdvertisingQueries(patch);
    setDisplaySpotlightRemoveModal(false);
  };

  const handleSpotlightReassignSuccess = () => {
    const patch = {
      buttonText: 'Remove Spotlight',
      buttonUrl: 'SPOTLIGHT_REMOVE',
      listingSpotlight: { isSpotlighted: true },
    };
    invalidateAdvertisingQueries(patch);
    setDisplaySpotlightReassignModal(false);
  };

  return (
    <>
      <Anchor href={targetListingRoute} onClick={handleRowClick} css={{ textDecoration: 'none', display: 'contents' }}>
        <Table.Row onClick={handleRowClick} data-eventid={ZRAM_PROPERTIES_PAGE_EVENT_IDS.PROPERTY_CARD_CLICK_ZRAM}>
          <Table.Cell css={leftDataCellStyles(isScrolledToLeft)}>
            <Box css={{ display: 'flex' }}>
              <Box data-loading={isComponentLoading || null} css={{ overflow: 'hidden', ...photoStyles }}>
                {!isComponentLoading && (
                  <PropertyCard.Photo
                    src={photoKey ? getPhotoUrls(isRunningInProd, photoKey).thumb : undefined}
                    alt={name}
                    fallbackImg={<PropertyCard.PhotoFallback />}
                    css={{
                      width: '2600',
                      height: '1800',
                    }}
                  />
                )}
              </Box>
              <Box css={addressWrapperStyles}>
                <LoadingParagraph
                  textStyle="body-sm-bold"
                  isLoading={isComponentLoading}
                  loadingWidth="12rem"
                  css={addressTextStyles}
                >
                  {name}
                </LoadingParagraph>
                {isMultifamily && (
                  <LoadingParagraph
                    textStyle="body-sm"
                    isLoading={isComponentLoading}
                    loadingWidth="12rem"
                    css={addressTextStyles}
                  >
                    {formattedStreet}
                  </LoadingParagraph>
                )}
                <LoadingParagraph
                  textStyle="body-sm"
                  isLoading={isComponentLoading}
                  loadingWidth="10rem"
                  css={addressTextStyles}
                >
                  {formattedCityStateZip}
                </LoadingParagraph>
                {!isScreenXxlGTE && !isArchivedView && renderAdvertisingCTA(false)}
              </Box>
            </Box>
          </Table.Cell>
          <Table.Cell css={hiddenMobileTableCellStyles}>
            {renderCellOrError(statusData, isStatusPending, renderStatusCell)}
          </Table.Cell>
          {!isArchivedView && (
            <>
              {!user?.isSingleFamilyEnterpriseCustomer && (
                <>
                  <Table.Cell css={hiddenMobileTableCellStyles}>
                    {isListingInactive
                      ? null
                      : renderCellOrError(
                          listingQualityScore,
                          isListingQualityScoreFetching,
                          (listingQualityScore, showLoading) => (
                            <Box css={listingQualityContainerStyles}>
                              <LoadingText
                                textStyle="body-sm"
                                css={listingQualityContentRecipe.raw({ rating: listingQualityScore?.rating })}
                                isLoading={showLoading}
                                loadingWidth="1200"
                              >
                                {listingQualityScore?.score}%
                              </LoadingText>
                            </Box>
                          ),
                        )}
                  </Table.Cell>
                  <Table.Cell css={{ ...hiddenMobileTableCellStyles, verticalAlign: 'middle' }}>
                    {isListingInactive
                      ? null
                      : renderCellOrError(
                          listingQualityScore,
                          isListingQualityScoreFetching,
                          (listingQualityScore, showLoading) => (
                            <>
                              {listingQualityScore?.missingDetails?.length > 0 ? (
                                listingQualityScore.missingDetails.map(renderMissingDetail)
                              ) : (
                                <LoadingText isLoading={showLoading} textStyle="body-sm">
                                  -
                                </LoadingText>
                              )}
                            </>
                          ),
                        )}
                  </Table.Cell>
                </>
              )}
              <Table.Cell css={hiddenMobileTableCellStyles}>
                {isListingInactive
                  ? null
                  : renderCellOrError(prospects, isProspectsFetching, (prospects, showLoading) => (
                      <LoadingText isLoading={showLoading} textStyle="body-sm">
                        {prospects?.count ? prospects.display : '-'}
                      </LoadingText>
                    ))}
              </Table.Cell>
              <Table.Cell css={hiddenMobileTableCellStyles}>
                {renderCellOrError(advertising, isAdvertisingFetching, (advertising, showLoading) => {
                  const titleOrDefault = advertising?.title || '-';
                  const hasSpotlightContext = advertising?.subTitle || advertising?.listingSpotlight?.isSpotlighted;
                  const advertisingTitle = hasSpotlightContext && titleOrDefault === '-' ? '' : titleOrDefault;

                  return (
                    <Box
                      css={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <LoadingText isLoading={showLoading} textStyle="body-sm">
                        {advertisingTitle}
                      </LoadingText>
                      {advertising?.subTitle && (
                        <Text textStyle="body-sm" color="text.subtle">
                          {advertising?.subTitle}
                        </Text>
                      )}
                      {advertising?.listingSpotlight?.isSpotlighted && (
                        <Tag size="sm" tone="blue" css={{ width: 'fit-content', whiteSpace: 'nowrap' }}>
                          Listing Spotlight
                        </Tag>
                      )}
                    </Box>
                  );
                })}
              </Table.Cell>
            </>
          )}
          <Table.Cell css={hiddenMobileTableCellStyles}>
            {renderCellOrError(sources, isSourcesPending, (sources, showLoading) => (
              <>
                {sources?.source === 'Zillow Rental Manager' && (
                  <Icon css={{ marginRight: '100' }}>
                    <IconZillowMark />
                  </Icon>
                )}
                <LoadingText
                  isLoading={showLoading}
                  textStyle={user?.isSingleFamilyEnterpriseCustomer ? 'body-sm-bold' : 'body-sm'}
                >
                  {sources?.source}
                </LoadingText>
                {sources?.additionalSources.length > 0 && (
                  <Text
                    textStyle={user?.isSingleFamilyEnterpriseCustomer ? 'body-sm-bold' : 'body-sm'}
                  >{` (${stringHelper.pluralize(sources?.additionalSources.length, 'other', 'others')})`}</Text>
                )}
                {sources?.activityMessage && <Paragraph textStyle="body-sm">{sources?.activityMessage}</Paragraph>}
              </>
            ))}
          </Table.Cell>
          <Table.Cell css={rightDataCellStyles}>
            <Box css={{ display: 'flex', gap: '200', alignItems: 'center', justifyContent: 'space-between' }}>
              {isScreenXxlGTE && !isArchivedView && renderAdvertisingCTA()}
              <Menu
                onOpenChange={handleMenuClick}
                trigger={
                  <IconButton
                    icon={<IconMoreFilled />}
                    title={`Actions for ${name}`}
                    tone="neutral"
                    emphasis="bare"
                    disabled={isComponentLoading || isOptionsPending}
                    onClick={(event) => {
                      event?.preventDefault();
                      event?.stopPropagation();
                    }}
                    css={{ marginLeft: 'auto', width: '1200' }}
                    data-eventid={PROPERTIES_PAGE_EVENT_IDS.PROPERTY_CARD_OVERFLOW_MENU}
                  />
                }
                content={options.map((option) => (
                  <Menu.Item
                    asChild={true}
                    key={option.type}
                    onClick={(event) => handleMenuItemClick(event, option)}
                    data-eventid={
                      option.type === AccountViewListingOptionType.RELEASE_CLAIM ? RELEASE_CLAIM_EVENT_ID : undefined
                    }
                  >
                    <a
                      href={option.ctaUrl}
                      target={option.type === AccountViewListingOptionType.EDIT_LISTING ? '_self' : '_blank'}
                      rel={option.type === AccountViewListingOptionType.EDIT_LISTING ? '' : 'noreferrer'}
                    >
                      <Menu.ItemLabel>{option.ctaText}</Menu.ItemLabel>
                    </a>
                  </Menu.Item>
                ))}
              />
            </Box>
          </Table.Cell>
        </Table.Row>
      </Anchor>
      {displayReleaseClaimModal && (
        <ReleaseClaimModal
          listing={{
            ...listing,
            adproduct: {
              unpaidCustomerId: hasAdvertisingButtons(advertising) ? advertising.unpaidCustomerId : '',
            },
          }}
          listingRoute={targetListingRoute}
          onClose={() => setDisplayReleaseClaimModal(false)}
        />
      )}
      {Boolean(displayArchivePropertyModal) && (
        <ArchivePropertyModal
          onClose={() => setDisplayArchivePropertyModal('')}
          isArchived={displayArchivePropertyModal !== AccountViewListingOptionType.ARCHIVE}
          listingAlias={listingAlias}
          address={address}
        />
      )}
      {displaySpotlightApplyModal && (
        <SpotlightApplyModal
          isOpen={displaySpotlightApplyModal}
          onClose={() => setDisplaySpotlightApplyModal(false)}
          onSuccess={handleSpotlightApplySuccess}
          listingAlias={listingAlias}
          listingName={name}
          availabilityText={spotlightAvailabilityText}
        />
      )}
      {displaySpotlightRemoveModal && (
        <SpotlightRemovalModal
          isOpen={displaySpotlightRemoveModal}
          onClose={() => setDisplaySpotlightRemoveModal(false)}
          listingAlias={listingAlias}
          listingName={name}
          availabilityText={spotlightAvailabilityText}
          onSuccess={handleSpotlightRemoveSuccess}
        />
      )}
      {displaySpotlightReassignModal && (
        <SpotlightReassignModal
          isOpen={displaySpotlightReassignModal}
          onClose={() => setDisplaySpotlightReassignModal(false)}
          listingAlias={listingAlias}
          listingName={name}
          availabilityText={spotlightAvailabilityText}
          onSuccess={handleSpotlightReassignSuccess}
        />
      )}
    </>
  );
};
